import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useCartStore, formatCOP } from '../store/cartStore';
import { X, Minus, Plus, ShoppingBag } from 'lucide-react';
import { CART } from '../constants/testIds';

const CartDrawer = ({ open, onOpenChange }) => {
  const { items, removeItem, updateQuantity, getSubtotal, getTotalBags } = useCartStore();
  const navigate = useNavigate();
  
  const handleCheckout = () => {
    if (items.length === 0) return;
    onOpenChange(false);
    navigate('/checkout');
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        data-testid={CART.drawer}
        className="w-full sm:max-w-lg bg-[#FDFBF7] border-l border-[#E5DEC5] flex flex-col"
      >
        <SheetHeader className="border-b border-[#E5DEC5] pb-4">
          <SheetTitle className="heading-serif text-2xl text-[#142A1D] font-light flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-[#B58A3F]" strokeWidth={1.5} />
            Tu Carrito
            {getTotalBags() > 0 && (
              <span className="text-sm text-[#B58A3F] font-normal">
                ({getTotalBags()} {getTotalBags() === 1 ? 'bolsa' : 'bolsas'})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 overflow-hidden">
          {items.length === 0 ? (
            <div 
              data-testid={CART.emptyMessage}
              className="flex-1 flex items-center justify-center text-center"
            >
              <div>
                <ShoppingBag className="w-16 h-16 text-[#B58A3F]/30 mx-auto mb-4" strokeWidth={1} />
                <p className="text-[#142A1D] opacity-60 text-lg mb-2">Tu carrito está vacío</p>
                <p className="text-[#142A1D] opacity-40 text-sm">Agrega algunos productos para comenzar</p>
              </div>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div 
                data-testid={CART.itemsList}
                className="flex-1 overflow-y-auto space-y-3 pr-2 pb-4"
              >
                <AnimatePresence mode="popLayout">
                  {items.map((item) => (
                    <motion.div
                      key={item.key}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex gap-4 p-4 bg-white rounded-xl border border-[#E5DEC5] shadow-sm"
                    >
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-20 h-20 object-contain rounded-lg bg-[#0a0a0a] flex-shrink-0"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#142A1D] text-sm mb-1">
                          {item.name}
                        </h3>
                        <div className="flex flex-wrap gap-1 mb-2">
                          <span className="inline-block px-2 py-0.5 bg-[#B58A3F]/10 text-[#B58A3F] rounded-md text-xs font-medium">
                            {item.profile}
                          </span>
                          <span className="inline-block px-2 py-0.5 bg-[#2B4C36]/10 text-[#2B4C36] rounded-md text-xs font-medium">
                            {item.grind}
                          </span>
                        </div>
                        <p className="text-[#B58A3F] font-medium text-sm">
                          {formatCOP(item.price)}
                        </p>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.key, item.quantity - 1)}
                            className="p-1 hover:bg-[#F2EBE1] rounded transition-colors"
                          >
                            <Minus className="w-3 h-3 text-[#142A1D]" />
                          </button>
                          <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.key, item.quantity + 1)}
                            className="p-1 hover:bg-[#F2EBE1] rounded transition-colors"
                          >
                            <Plus className="w-3 h-3 text-[#142A1D]" />
                          </button>
                        </div>
                      </div>
                      
                      <button
                        data-testid={CART.removeItemButton}
                        onClick={() => removeItem(item.key)}
                        className="p-2 hover:bg-[#F2EBE1] rounded-lg h-fit transition-colors"
                        aria-label="Eliminar"
                      >
                        <X className="w-4 h-4 text-[#142A1D]" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              
              {/* Total and Checkout */}
              <div className="border-t border-[#E5DEC5] pt-6 pb-2 space-y-4 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <span className="text-base font-medium text-[#142A1D]">Subtotal</span>
                  <span 
                    data-testid={CART.totalAmount}
                    className="text-2xl font-light text-[#142A1D] heading-serif"
                  >
                    {formatCOP(getSubtotal())}
                  </span>
                </div>
                <p className="text-xs text-[#142A1D] opacity-60 text-center">
                  El envío se calculará en el siguiente paso
                </p>
                
                <motion.button
                  data-testid={CART.checkoutButton}
                  onClick={handleCheckout}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full btn-primary"
                >
                  Continuar al Pago
                </motion.button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
